import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import Pagination from '../../components/Common/Pagination/Pagination';
import {
  Search,
  Calendar,
  Download,
  FileText,
  FileCode,
  Plus,
  ChevronRight,
  MoreVertical,
  CheckCircle2,
  Clock,
  RotateCcw,
  AlertCircle,
  Pencil,
  Trash2,
  Eye,
  SlidersHorizontal as Filters,
  X,
  Loader2
} from 'lucide-react';
import styles from './DesignSubmissions.module.css';
import Footer from '../../components/Common/Footer/Footer';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import NoData from '../../components/Common/NoData/NoData';
import HistoryModal from '../../components/Projects/HistoryModal';
import IntimateModal from '../../components/Projects/IntimateModal';
import { Send } from 'lucide-react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const DesignSubmissions = () => {
  const navigate = useNavigate();
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = (storedUser.role || '').toLowerCase() === 'admin';
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    project_id: '',
    vendor_id: '',
    status: ''
  });
  const [filterData, setFilterData] = useState({
    projects: [],
    vendors: []
  });

  // History Modal State
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);

  // Intimate Modal State
  const [isIntimateModalOpen, setIsIntimateModalOpen] = useState(false);
  const [selectedIntimateItem, setSelectedIntimateItem] = useState(null);

  const fetchFilterData = async () => {
    try {
      const [projectsRes, vendorsRes] = await Promise.all([
        api.get('/projects-dropdown'),
        api.get('/vendors-dropdown')
      ]);
      setFilterData({
        projects: projectsRes.data.data || [],
        vendors: vendorsRes.data.data || []
      });
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  useEffect(() => {
    fetchFilterData();
  }, []);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/design-submissions', {
        params: {
          page: currentPage,
          per_page: itemsPerPage,
          search: searchQuery,
          ...filters
        }
      });
      if (response.data.success) {
        setSubmissions(response.data.data.data || []);
        setTotalItems(response.data.data.total || 0);
        setCurrentPage(response.data.data.current_page || 1);
      }
    } catch (error) {
      console.error('Error fetching design submissions:', error);
      toast.error('Failed to load design submissions');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchQuery, filters]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchSubmissions();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [fetchSubmissions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.action-wrapper')) {
        setOpenMenuIndex(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const toggleActionMenu = (index, e) => {
    e.stopPropagation();
    if (openMenuIndex === index) {
      setOpenMenuIndex(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom + 4, left: rect.right - 160 });
      setOpenMenuIndex(index);
    }
  };

  const getStatusClass = (status) => {
    if (!status) return styles['status-pending'];
    switch (status.toLowerCase()) {
      case 'approved':
      case 'code1': return styles['status-code1'];
      case 'code2': return styles['status-code2'];
      case 'code3': return styles['status-code3'];
      case 'rejected':
      case 'code4': return styles['status-code4'];
      case 'pending':
      case 'in_review': return styles['status-pending'];
      case 'in_revision':
      case 'revision': return styles['status-revision'];
      case 'intimate': return styles['status-intimate'];
      default: return styles['status-pending'];
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handlePerPageChange = (option) => {
    setItemsPerPage(parseInt(option.value));
    setCurrentPage(1);
  };

  const handleFilterChange = (key, option) => {
    setFilters(prev => ({ ...prev, [key]: option ? option.value : '' }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      project_id: '',
      vendor_id: '',
      status: ''
    });
    setSearchQuery('');
    setCurrentPage(1);
  };

  const handleLOIClick = (item) => {
    setSelectedHistoryItem(item);
    setIsHistoryModalOpen(true);
  };

  const handleIntimateClick = (item) => {
    setSelectedIntimateItem(item);
    setIsIntimateModalOpen(true);
    setOpenMenuIndex(null);
  };

  const handleDelete = async (item) => {
    setOpenMenuIndex(null);

    MySwal.fire({
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: '<div style="display: flex; align-items: center; justify-content: center; gap: 8px;"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg> Delete Submission</div>',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      customClass: {
        popup: 'custom-swal-popup',
        actions: 'custom-swal-actions',
        confirmButton: 'custom-swal-confirm',
        cancelButton: 'custom-swal-cancel',
      },
      html: `
        <style>
          .custom-swal-popup {
            border-radius: 16px !important;
            padding: 32px 24px 24px 24px !important;
            font-family: 'Outfit', sans-serif !important;
            width: 440px !important;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
          }
          @media (max-width: 480px) {
            .custom-swal-popup {
              width: calc(100vw - 32px) !important;
              padding: 24px 16px 20px 16px !important;
            }
            .custom-swal-actions {
              flex-direction: column !important;
              gap: 8px !important;
            }
            .custom-swal-confirm,
            .custom-swal-cancel {
              width: 100% !important;
              justify-content: center !important;
            }
          }
          .custom-swal-actions {
            display: flex !important;
            gap: 12px !important;
            margin-top: 24px !important;
            width: 100% !important;
            padding: 0 !important;
          }
          .custom-swal-confirm {
            background-color: #dc2626 !important;
            color: white !important;
            border: none !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
          }
          .custom-swal-confirm:hover {
            background-color: #b91c1c !important;
          }
          .custom-swal-cancel {
            background-color: #ffffff !important;
            color: #475569 !important;
            border: 1px solid #cbd5e1 !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
          }
          .custom-swal-cancel:hover {
            background-color: #f8fafc !important;
          }
          .custom-icon-wrapper {
            width: 64px;
            height: 64px;
            background-color: #fee2e2;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 20px;
          }
        </style>
        <div class="custom-icon-wrapper">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <h2 style="font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 12px 0;">Delete Design Submission?</h2>
        <p style="margin: 0 0 24px 0; color: #475569; font-size: 14px; line-height: 1.5;">
          Are you sure you want to delete version <strong>v${item.version_number || '1.0'}</strong> for LOI <strong>${item.letter_of_intent?.loi_number}</strong>?<br/>
          This action cannot be undone and will remove all version history.
        </p>
      `
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.delete(`/design-submissions/${item.id}`);
          if (response.data.success) {
            toast.success('Design submission deleted successfully');
            fetchSubmissions();
          }
        } catch (error) {
          console.error('Error deleting submission:', error);
          toast.error(error.response?.data?.message || 'Failed to delete submission');
        }
      }
    });
  };

  const activeFiltersCount = Object.values(filters).filter(v => v !== '').length;

  return (
    <div className={styles['design-submissions-container']}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Design Submissions</h1>
              <p>Review and manage technical drawings across all LOIs (Letter of Intent)</p>
            </div>
            <button
              className={styles['add-btn']}
              onClick={() => navigate('/projects/design-submissions/upload')}
            >
              <Plus size={20} />
              <span>Upload New Design</span>
            </button>
          </div>

          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={18} />
              <input
                type="text"
                placeholder="Search drawings, vendors, or LOI number..."
                className={styles['search-input']}
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </div>
            <div className={styles['filter-actions']}>
              <div className={styles['per-page-select-wrapper']}>
                <CustomSelect
                  isSearchable={false}
                  backgroundColor="white"
                  value={{ value: itemsPerPage, label: `${itemsPerPage} per page` }}
                  onChange={handlePerPageChange}
                  options={[
                    { value: 10, label: '10 per page' },
                    { value: 25, label: '25 per page' },
                    { value: 50, label: '50 per page' },
                    { value: 100, label: '100 per page' }
                  ]}
                />
              </div>
              <button
                className={`${styles['filter-btn']} ${showFilters ? styles['active'] : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filters size={18} />
                <span>Filters</span>
                {activeFiltersCount > 0 && <span className={styles['filter-badge']} />}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={styles['filters-panel']}>
              <div className={styles['filters-header']}>
                <div className={styles['filters-title-group']}>
                  <h3>Filter Submissions</h3>
                  <p>Refine the list by project, vendor, or status</p>
                </div>
                <button
                  className={styles['close-icon-btn']}
                  onClick={() => setShowFilters(false)}
                >
                  <X size={18} />
                </button>
              </div>

              <div className={styles['filters-grid']}>
                <div className={styles['filter-group']}>
                  <label>By Project</label>
                  <CustomSelect
                    value={filters.project_id ? {
                      value: filters.project_id,
                      label: filterData.projects.find(p => p.id.toString() === filters.project_id)?.project_name || 'All Projects'
                    } : null}
                    onChange={(opt) => handleFilterChange('project_id', opt)}
                    options={filterData.projects.map(p => ({ value: p.id.toString(), label: p.project_name }))}
                    placeholder="All Projects"
                    isClearable={true}
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>By Vendor</label>
                  <CustomSelect
                    value={filters.vendor_id ? {
                      value: filters.vendor_id,
                      label: filterData.vendors.find(v => v.id.toString() === filters.vendor_id)?.vendor_name || 'All Vendors'
                    } : null}
                    onChange={(opt) => handleFilterChange('vendor_id', opt)}
                    options={filterData.vendors.map(v => ({ value: v.id.toString(), label: v.vendor_name }))}
                    placeholder="All Vendors"
                    isClearable={true}
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>By Status</label>
                  <CustomSelect
                    value={filters.status ? {
                      value: filters.status,
                      label: filters.status.charAt(0).toUpperCase() + filters.status.slice(1).replace('_', ' ')
                    } : null}
                    onChange={(opt) => handleFilterChange('status', opt)}
                    options={[
                      { value: 'pending', label: 'Pending' },
                      { value: 'in_review', label: 'In Review' },
                      { value: 'code1', label: 'CODE 1' },
                      { value: 'code2', label: 'CODE 2' },
                      { value: 'code3', label: 'CODE 3' },
                      { value: 'code4', label: 'CODE 4' },
                      { value: 'in_revision', label: 'In Revision' }
                    ]}
                    placeholder="All Statuses"
                    isClearable={true}
                  />
                </div>
              </div>

              <div className={styles['filters-footer']}>
                <button className={styles['reset-btn']} onClick={resetFilters}>
                  Reset
                </button>
                <button className={styles['filters-close-btn']} onClick={() => setShowFilters(false)}>
                  Apply Filters
                </button>
              </div>
            </div>
          )}

          <div className={[styles['custom-table-container'], styles['full-height'], 'card'].join(' ')}>
            <div className={styles['custom-table-wrapper']}>
              <table className={styles['custom-table']}>
                <thead>
                  <tr>
                    <th>VERSION</th>
                    <th>PROJECT</th>
                    <th>LOI NUMBER</th>
                    <th>VENDOR</th>
                    <th>FILE NAME</th>
                    <th>SUBMITTED</th>
                    <th>STATUS</th>
                    <th>REVIEWED BY</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={10} style={{ textAlign: 'center', padding: '100px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                          <Loader2 size={40} className={styles['spinner']} style={{ color: '#f97316' }} />
                          <span style={{ color: '#64748b', fontWeight: 600 }}>Fetching technical details...</span>
                        </div>
                      </td>
                    </tr>
                  ) : submissions.length > 0 ? (
                    submissions.map((item, index) => (
                      <tr key={item.id || index}>
                        <td
                          className={[styles['version-cell'], styles['clickable']].join(' ')}
                          onClick={() => navigate(`/projects/design-submissions/review/${item.id}`)}
                        >
                          v{item.version_number || '1.0'}
                        </td>
                        <td className={styles['project-name-cell']}>
                          <span>{item.letter_of_intent?.project?.project_name || '---'}</span>
                        </td>
                        <td className={styles['po-num-cell']}>
                          <span
                            className={styles['po-link']}
                            onClick={() => handleLOIClick(item)}
                            title="View Revision History"
                          >
                            {item.letter_of_intent?.loi_number || '---'}
                          </span>
                        </td>
                        <td className={styles['bold-cell']}>{item.vendor?.vendor_name || '---'}</td>
                        <td className={styles['file-name-cell']}>
                          <FileText size={18} className={styles['pdf-icon']} />
                          {item.pdf_filename || '---'}
                        </td>
                        <td className={styles['submitted-date-cell']}>
                          {item.consultant_rev_sub_to_client_date ? new Date(item.consultant_rev_sub_to_client_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '---'}
                        </td>
                        <td>
                          <span className={`${styles['status-badge']} ${getStatusClass(item.status)}`}>
                            {(item.status || 'pending').replace('_', ' ').toUpperCase()}
                          </span>
                        </td>
                        <td className={item.creator ? 'reviewer-cell' : 'reviewer-cell unassigned'}>
                          {item.creator?.name || 'Unassigned'}
                        </td>
                        <td className={styles['action-cell']}>
                          <div className={`action-wrapper ${styles['action-wrapper']}`}>
                            <button
                              className={`${styles['icon-btn']} ${openMenuIndex === index ? styles['active'] : ''}`}
                              onClick={(e) => toggleActionMenu(index, e)}
                            >
                              <MoreVertical size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <NoData message="No design submissions found." colSpan={10} isTable={true} />
                  )}
                </tbody>
              </table>
            </div>

            <div className={styles['design-cards']}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Loader2 size={32} className={styles['spinner']} style={{ color: '#f97316' }} />
                </div>
              ) : submissions.length > 0 ? (
                submissions.map((item, index) => (
                  <div key={item.id || index} className={styles['design-card']}>
                    <div className={styles['card-main']}>
                      <div className={styles['card-info']}>
                        <span className={styles['card-id']} onClick={() => navigate(`/projects/design-submissions/review/${item.id}`)}>
                          v{item.version_number || '1.0'}
                        </span>
                        <span className={`${styles['status-pill']} ${getStatusClass(item.status)}`}>
                          {(item.status || 'pending').replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      <div className={`action-wrapper ${styles['action-wrapper']}`}>
                        <button
                          className={styles['action-btn']}
                          onClick={(e) => toggleActionMenu(index, e)}
                        >
                          <MoreVertical size={18} />
                        </button>
                      </div>
                    </div>
                    <div className={styles['card-details']}>
                      <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                        <span className={styles['detail-label']}>Project</span>
                        <span className={styles['detail-value']}>{item.letter_of_intent?.project?.project_name || '---'}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>LOI Number</span>
                        <span
                          className={styles['po-link']}
                          onClick={() => handleLOIClick(item)}
                        >
                          {item.letter_of_intent?.loi_number || '---'}
                        </span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Vendor</span>
                        <span className={styles['detail-value']}>{item.vendor?.vendor_name || '---'}</span>
                      </div>
                      <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                        <span className={styles['detail-label']}>File Name</span>
                        <div className={styles['file-name-mobile']}>
                          <FileText size={14} className={styles['pdf-icon']} />
                          <span className={styles['detail-value']}>{item.pdf_filename || '---'}</span>
                        </div>
                      </div>
                    </div>
                    <div className={styles['card-footer']}>
                      <div className={styles['footer-left']}>
                        <Calendar size={14} className={styles['text-muted']} />
                        <span>{item.consultant_rev_sub_to_client_date ? new Date(item.consultant_rev_sub_to_client_date).toLocaleDateString('en-IN') : '---'}</span>
                      </div>
                      <div className={styles['footer-right']}>
                        <span className={styles['reviewer-text']}>By: {item.creator?.name || 'Unassigned'}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <NoData message="No design submissions found." isTable={false} />
              )}
            </div>

            <div className={styles['desktop-pagination']}>
              <Pagination
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          </div>

          <div className={styles['mobile-pagination']}>
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>

          {/* Global Fixed Action Menu */}
          {openMenuIndex !== null && (
            <div
              className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
              style={{
                position: 'fixed',
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
                zIndex: 9999
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {!['code1', 'code2', 'code3', 'code4', 'approved', 'rejected', 'intimate', 'intemate'].includes(submissions[openMenuIndex]?.status?.toLowerCase()) && (
                <button className={styles['dropdown-item']} onClick={() => navigate(`/projects/design-submissions/review/${submissions[openMenuIndex]?.id}`)}>
                  <CheckCircle2 size={16} /> Review
                </button>
              )}

              {['code2', 'code3', 'code4', 'rejected'].includes(submissions[openMenuIndex]?.status?.toLowerCase()) && (
                <button className={styles['dropdown-item']} onClick={() => handleIntimateClick(submissions[openMenuIndex])}>
                  <Send size={16} /> Intimate to Vendor
                </button>
              )}

              {isAdmin && (
                <button className={styles['dropdown-item']} onClick={() => navigate(`/projects/design-submissions/edit/${submissions[openMenuIndex]?.id}`)}>
                  <Pencil size={16} /> Edit
                </button>
              )}

              {!['rejected', 'approved', 'code1', 'code2', 'code3', 'code4'].includes(submissions[openMenuIndex]?.status?.toLowerCase()) && (
                <>
                  <div className={styles['dropdown-divider']}></div>
                  <button className={[styles['dropdown-item'], styles['delete-opt']].join(' ')} onClick={() => handleDelete(submissions[openMenuIndex])}>
                    <Trash2 size={16} /> Delete
                  </button>
                </>
              )}
            </div>
          )}

          <HistoryModal
            isOpen={isHistoryModalOpen}
            onClose={() => setIsHistoryModalOpen(false)}
            submissionId={selectedHistoryItem?.id}
            poNumber={selectedHistoryItem?.letter_of_intent?.loi_number}
          />

          <IntimateModal
            isOpen={isIntimateModalOpen}
            onClose={() => setIsIntimateModalOpen(false)}
            submission={selectedIntimateItem}
            onSuccess={fetchSubmissions}
          />

          <Footer />
        </main>
      </div>
    </div>
  );
};

export default DesignSubmissions;
